import React, { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { FaPlus, FaTrash, FaCheck } from 'react-icons/fa'
import { Stack, Wrap, WrapItem, Box, Tooltip, Badge, Button, Heading, Text, SimplePopover, Input, Alert, SimpleModal, SimpleTable, UserChip, EmptyBox, EmptyCard, ConfirmButton, PermissionChooser, BottomStickyBar } from '@/components/lib'
import api from '@/api'
import useStore from '@/store'
import utils from '@/utils/utils'
import useTitle from '@/hooks/useTitle'

import CollaborationImage from '@/images/collaboration.png'

const DEFAULT_PERMISSION = 'trial.access'

export default function CollaborationSettings () {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [manageCollaborator, setManageCollaborator] = useState()
  const { user, trial, team, getRolesForTrial, addRole, addTrialUser, removeTrialUser, getTrialUsers, sites, groups } = useStore()
  useTitle('Personnel', trial)
  const trialId = trial?._id
  const teamId = trial?.team
  const roles = getRolesForTrial(trialId)
  const collaborators = getTrialUsers(trialId)

  useEffect(() => {
    api.roles.getForTrial(trial._id, d => d.roles?.forEach(addRole))
  }, [trialId])

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) return
    api.teams.searchUsers(teamId, searchTerm, ({ users }) => setSearchResults(users))
  }, [teamId, searchTerm])

  const addCollaborator = (newCollaborator) => {
    api.users.updatePermissions(newCollaborator._id, trial._id, [DEFAULT_PERMISSION], data => {
      newCollaborator.permissions = Object.assign({}, { [trial._id]: data.permissions })
      addTrialUser(trialId, newCollaborator)
      setSearchTerm('')
      setSearchResults([])
    }, err => {
      utils.toast('error', 'Unable to update this user', err.message)
    })
  }

  const removeCollaborator = c => {
    api.users.updatePermissions(c._id, trial?._id, [], data => {
      removeTrialUser(trialId, c._id)
    })
  }

  const updateCollaboratorPermissions = (collaboratorId, permissions) => {
    const collaborator = collaborators.find(c => c._id === collaboratorId)
    addTrialUser(trialId, Object.assign({}, collaborator, { permissions }))
  }

  const updateCollaboratorRoles = (collaboratorId, roles) => {
    const collaborator = collaborators.find(c => c._id === collaboratorId)
    addTrialUser(trialId, Object.assign({}, collaborator, { roles }))
  }

  const canModifyPermission = c => {
    if (!utils.canManageTrial(user, trial)) return false
    return true
  }

  return (
    <Stack gap={5}>
      <Stack direction={{ base: 'column', sm: 'row' }} justifyContent='space-between'>
        <Box>
          <Heading size='md'>Manage personnel</Heading>
          <Text>Manage who can access this trial.</Text>
        </Box>
        {utils.canManageTrial(user, trial) &&
          <Box display='flex' justifyContent='end'>
            <SimplePopover
              trigger={<Button colorPalette='primary'>Add personnel to the trial</Button>}
              header={
                <Box>
                  <Heading size='md' as='h4'>Add someone to help manage {trial?.name}</Heading>
                </Box>
}
              body={
                <Stack>
                  <Text>Search for a member of your team to add them to your trial. You can search by name or email address.</Text>
                  <Input placeholder='Begin typing...' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  {searchTerm?.length > 2 && searchResults?.length === 0 &&
                    <Alert status='warning' variant='left-accent' size='xs'>No users match your search</Alert>}
                  {searchResults?.length > 0 &&
                    <SimpleTable
                      size='sm'
                      rows={searchResults.map(res => [
                        <UserChip user={res} compact key={res._id} />,
                        <Text key={res._id}>{utils.canAccessTrial(res, trial) ? <Badge colorPalette='green'>Added</Badge> : <Button colorPalette='primary' size='xs' onClick={() => addCollaborator(res)}><FaPlus /> Add</Button>}</Text>
                      ])}
                    />}
                </Stack>
  }
            />
          </Box>}
      </Stack>

      {utils.canManageTrial(user, trial) &&
        <EmptyCard icon='people' title='Add people to help manage your trial project' description={`Existing members of ${team?.name} can be added as personnel to help manage this trial. Once added, you can manage their roles at the trial-level or at a site-level.`}>
          {utils.canManageTeam(user, team) &&
            <Button colorPalette='primary' variant='ghost' as={Link} to='/team/users' minW='200px'>Manage team members</Button>}
        </EmptyCard>}

      {collaborators?.length === 0
        ? (
          <EmptyBox image={CollaborationImage} title='No personnel added yet' description='Add team members to help manage this trial.' />)
        : (

          <SimpleTable
            asCard
            tableProps={{ size: 'sm' }}
            headers={['Name', 'Roles', 'Permissions', '']}
            rows={collaborators?.map(c => [
              <UserChip user={c} key={c._id} />,
              <Stack key={c._id}>
                {c.roles?.length > 0
                  ? c.roles.map(r => {
                    const role = roles?.find(role => role._id === r)
                    return <Badge key={r} size='sm'>{role?.name}</Badge>
                  })
                  : <Text color='fg.subtle' fontSize='sm'>No roles assigned</Text>}
              </Stack>,
              <Stack gap={2} key={c._id}>
                <Wrap gap={1}>
                  {utils.hasPermission(c, 'root', 'global') && <WrapItem><Tooltip placement='top' label='This user is your team account manager'><Badge size='xs' colorPalette='blue'>Account manager</Badge></Tooltip></WrapItem>}
                  {utils.hasPermission(c, 'trials.manage', trial?.team) && <WrapItem><Tooltip placement='top' label='This user can manage all trials'><Badge size='xs' colorPalette='primary'>Team-level trial admin</Badge></Tooltip></WrapItem>}
                </Wrap>
                <Wrap gap={1}>
                  {c.permissions && c.permissions[trial._id]?.map(p => {
                    return <WrapItem key={p}><Badge key={p} colorPalette='gray' size='xs'>{p}</Badge></WrapItem>
                  })}
                  {groups?.map(g => {
                    const count = (c.permissions && c.permissions[g._id]?.length) || 0
                    if (count > 0) return <WrapItem key={g._id}><Badge key={g._id} size='xs'>{g.name} ({count} permissions)</Badge></WrapItem>
                    return null
                  })}
                  {sites?.map(s => {
                    const count = (c.permissions && c.permissions[s._id]?.length) || 0
                    if (count > 0) return <WrapItem key={s._id}><Badge key={s._id} size='xs'>{s.name} ({count} permissions)</Badge></WrapItem>
                    return null
                  })}
                </Wrap>
              </Stack>,
              <Stack direction='row' justifyContent='end' key={c._id}>
                {canModifyPermission(c) &&
                  <>
                    <Button colorPalette='primary' size='sm' variant='outline' onClick={() => setManageCollaborator(c)}>Manage</Button>
                    <ConfirmButton
                      trigger={<Button variant='ghost' size='sm'><FaTrash /></Button>}
                      header={`Really remove ${c.firstName || 'this user'} from the trial?`}
                      footer='To give the user access again a team admin will need to re-add them to the trial.'
                      onConfirm={() => removeCollaborator(c)}
                    />
                  </>}
              </Stack>
            ])}
          />)}

      <CollaboratorEditor collaborator={manageCollaborator} onUpdateRoles={updateCollaboratorRoles} onUpdatePermissions={updateCollaboratorPermissions} onClose={() => setManageCollaborator(null)} trial={trial} groups={groups} sites={sites} roles={roles} />
    </Stack>
  )
}

function CollaboratorEditor ({ collaborator, onUpdateRoles, onUpdatePermissions, onClose, trial, groups, sites, roles }) {
  const [permissions, setPermissions] = useState({})
  const [assignedRoles, setAssignedRoles] = useState([])

  useEffect(() => {
    if (collaborator) {
      setAssignedRoles(collaborator.roles || [])
      setPermissions(collaborator.permissions || {})
    }
  }, [collaborator])

  const addRole = (roleId) => {
    api.users.addRole(collaborator._id, roleId, data => {
      const newRoles = Object.assign([], assignedRoles || [])
      newRoles.push(roleId)
      setAssignedRoles(newRoles)
      onUpdateRoles(collaborator._id, newRoles)
    }, err => {
      utils.toast('error', 'Unable to add this role', err.message)
    })
  }
  const removeRole = (roleId) => {
    api.users.removeRole(collaborator._id, roleId, data => {
      const newRoles = Object.assign([], assignedRoles || [])
      const index = newRoles.indexOf(roleId)
      if (index > -1) newRoles.splice(index, 1)
      setAssignedRoles(newRoles)
      onUpdateRoles(collaborator._id, newRoles)
    }, err => {
      utils.toast('error', 'Unable to remove this role', err.message)
    })
  }

  const onPermissionScopedChange = (scope, newPermissions) => {
    api.users.updatePermissions(collaborator._id, scope, newPermissions, data => {
      setPermissions({ ...permissions, [scope]: data.permissions })
      onUpdatePermissions(collaborator._id, { ...permissions, [scope]: data.permissions })
    })
  }

  if (!collaborator) return null

  return (
    <SimpleModal
      isOpen={!!collaborator} onClose={onClose} size='lg'
      header={`Manage ${collaborator.firstName} ${collaborator.lastName}'s permissions for ${trial.name}`}
      body={
        <Stack gap={5}>
          {roles?.length > 0 &&
            <Stack gap={3} p={3} bg='bg.surface'>
              <Heading size='sm'>Role assignments</Heading>
              <SimpleTable
                size='sm'
                headers={['Role name', '']}
                rows={roles.map(role => [
                  role.name,
                  <Box key={role._id} display='flex' justifyContent='end'>
                    {assignedRoles?.includes(role._id)
                      ? <Button size='sm' colorPalette='green' onClick={() => removeRole(role._id)}><FaCheck /> Assigned</Button>
                      : <Button size='sm' colorPalette='primary' variant='outline' onClick={() => addRole(role._id)}><FaPlus /> Assign role</Button>}
                  </Box>
                ])}
              />
            </Stack>}

          <Box p={3} bg='bg.subtle'>
            <PermissionChooser trial={trial} groups={groups} sites={sites} permissions={permissions} onScopedChange={onPermissionScopedChange} />
          </Box>

          <BottomStickyBar justifyContent='end'>
            <Button colorPalette='primary' variant='solid' onClick={onClose}>Finish</Button>
          </BottomStickyBar>
        </Stack>
}
    />
  )
}
